import React, { memo, useEffect, useRef, useState } from 'react';
import { Button, Stack } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

import { Editor, CardWrapper, useHookForm } from '@monorepo/common';

import { ArrowRight as ArrowRightIcon } from '../../../../components/Icons/arrow-right';
import { IEventsStepProps } from '../../interfaces';
import { IStep3Form } from './formInterface';
import { schema } from './schema';
import { useCommitOnLeave } from '../useCommitOnLeave';
import { ShortDescriptionField } from '../../../../components/ShortDescriptionField';
import { replaceLineBreaks } from '../../../../utils/shortDescription';
import JobDescriptionTitle from '../../../../components/JobStepper/components/JobDescriptionTitle';

interface IStep4Props extends IEventsStepProps {
  defaultData: IStep3Form | null,
  setStep3: (value: IStep3Form) => void,
  onSave?: (value: IStep3Form) => void,
  onDraftChange?: (value: IStep3Form) => void,
  onEditorNormalize?: (given: string, rewritten: string) => void,
  registerCommit?: (commitNow: () => void) => () => void,
}

const empty:string = '<p><br></p>';
export const Step3 = memo(({
  onBack,
  onNext,
  onSave,
  setStep3,
  defaultData,
  onDraftChange,
  onEditorNormalize,
  registerCommit,
  Nav,
}:IStep4Props) => {
  const submitIntent = useRef<'next' | 'save'>('next');
  const [editorValue, setEditorValue] = useState<string>(defaultData?.jobDescription || empty);
  // The HTML last handed to the editor as its value. react-quill can call onChange with the
  // props of an earlier render, so a closure over editorValue may be a render behind.
  const editorInput = useRef(editorValue);
  editorInput.current = editorValue;
  const [changedFields, setChangedFields] = useState< IStep3Form | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    errors
  } = useHookForm<IStep3Form>({
    schema,
    showChangedFields: (a) => {
      setChangedFields(a);
      onDraftChange?.(a);
    }
  });
  const editorChange = (res: string, source: 'api' | 'user' | 'silent') => {
    const given = editorInput.current;
    setEditorValue(res);
    // Quill changes content itself for paste, undo and toolbar actions; browser
    // input/change events do not reliably represent these edits. Every user edit, paste
    // included (see the shared Editor), arrives as 'user'.
    if (source !== 'user') {
      // An 'api' change is Quill rewriting HTML it was given, not an edit. Loading saved
      // HTML can take two passes: one while the editor mounts, and a second when react-quill
      // re-applies the value on the next render (removing extra spaces, then a trailing
      // empty paragraph). The parent keeps a real pending edit's baseline.
      onEditorNormalize?.(given, res.trim());
      return;
    }
    onDraftChange?.({ ...getValues(), jobDescription: res.trim() });
  };

  const markCommitted = useCommitOnLeave(() => {
    const values = getValues();
    if (JSON.stringify(values) !== JSON.stringify(defaultData)) setStep3(values);
  }, registerCommit);

  const submit = (data:IStep3Form) => {
    if (setStep3) {
      setStep3(data);
      if (submitIntent.current === 'save') {
        onSave?.(data);
      } else {
        markCommitted();
        onNext?.();
      }
      submitIntent.current = 'next';
    }
  };

  const goBack = () => {
    if (onBack) {
      if (changedFields && (JSON.stringify(defaultData).toString() !== JSON.stringify(changedFields))) {
        setStep3(changedFields);
      }
      markCommitted();
      onBack();
    }
  };

  useEffect(() => {
    if (editorValue === '<p><br></p>') {
      setValue('jobDescription', null);
      return;
    }
    setValue('jobDescription', editorValue.trim());
  }, [editorValue]);

  useEffect(() => {
    if (defaultData) {
      setValue('jobDescription', defaultData.jobDescription === '<p><br></p>' ? null : defaultData.jobDescription);
      setValue('shortDescription', defaultData.shortDescription && replaceLineBreaks(defaultData.shortDescription));
    }
  }, []);

  return (
    <CardWrapper>
      <JobDescriptionTitle />
      <form
        onSubmit={handleSubmit(submit)}
        data-cy="step3"
      >
        <Editor
          isRequired
          value={editorValue}
          onChange={editorChange}
          error={errors.jobDescription ? (errors.jobDescription.message as string | null) : null}
          fieldCommentBottom="We recommend including the job description, job requirements, and candidate qualifications as well as some information about the company."
        />

        <ShortDescriptionField
          extraProps={{ ...register('shortDescription') }}
          errors={errors}
        />

        <Nav
          nextDisabled={false}
          onBack={goBack}
          nextButton={(
            <Stack
              className="cpg-edit-step-actions"
              direction={{ xs: 'column', sm: 'row' }}
            >
              <Button
                startIcon={<SaveIcon />}
                variant="outlined"
                type="submit"
                onClick={() => {
                  submitIntent.current = 'save';
                }}
                data-cy="saveListing"
              >
                Save Listing
              </Button>
              <Button
                endIcon={(<ArrowRightIcon fontSize="small" />)}
                variant="contained"
                disabled={false}
                type="submit"
                onClick={() => {
                  submitIntent.current = 'next';
                }}
                data-cy="nextStep"
              >
                Next Step
              </Button>
            </Stack>
                )}
        />
      </form>
    </CardWrapper>
  );
});
